import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    Activity,
    Clock,
    CheckCircle,
    AlertTriangle,
    Eye,
    Trash2,
    Search,
    Calendar,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Building,
    CircleDot,
    Timer,
    UserCircle,
    FileText,
    History,
    Hash,
    Monitor,
    Smartphone
} from 'lucide-react';
import './JobListPage.css';
import { useJobs, useJobFilterOptions, useApiMutation } from '../hooks/useApi';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { jobsApi } from '../services/api';
import { useToast } from '../components/toast/ToastProvider';

interface JobCreator {
    id: string;
    name?: string;
    username?: string;
    email?: string;
    role?: string;
    displayName: string;
}

interface ProcessingJob {
    id: string;
    urlCount: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    progress: number;
    startTime: Date | null;
    endTime?: Date;
    processedUrls: number;
    violationsFound: number;
    errors: number;
    jobName?: string;
    guidelineId?: string;
    guidelineName?: string;
    version?: number | null;
    asp?: string;
    operationCompany?: string;
    media?: string;
    createdBy?: JobCreator;
    deviceMode?: 'personal_computer' | 'smart_phone';
    enableSearch?: boolean;
    searchTimeRangeMode?: string | null;
    searchDateFrom?: string | null;
    searchDateTo?: string | null;
}

function formatJobSearchRangeText(job: ProcessingJob): string {
    if (job.searchTimeRangeMode === 'absolute' && job.searchDateFrom && job.searchDateTo) {
        const fromFmt = new Date(`${job.searchDateFrom}T00:00:00`).toLocaleDateString('ja-JP');
        const toFmt = new Date(`${job.searchDateTo}T00:00:00`).toLocaleDateString('ja-JP');
        return `${fromFmt} 〜 ${toFmt}`;
    }
    if (job.searchTimeRangeMode === 'none') {
        return '指定なし（上位順）';
    }
    return '—';
}

/** Parse comma-separated query param (e.g. guidelines=id1,id2). */
function parseListParamFromSearchParams(sp: URLSearchParams, key: string): string[] {
    const raw = sp.get(key);
    if (!raw) return [];
    return raw
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
}

const ALL_JOB_STATUS_LABELS = ['保留中', '処理中', '完了', '失敗'];

const STATUS_LABEL_TO_API: { [key: string]: string } = {
    '保留中': 'pending',
    '処理中': 'processing',
    '完了': 'completed',
    '失敗': 'failed'
};

const JobListPage: React.FC = () => {
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    // Local state — initialize from URL on first render so URL sync cannot clear params before hydration.
    const [jobs, setJobs] = useState<ProcessingJob[]>([]);
    const [currentPage, setCurrentPage] = useState(() => {
        const p = parseInt(searchParams.get('page') || '1', 10);
        return !Number.isNaN(p) && p > 0 ? p : 1;
    });
    const [selectedStatuses, setSelectedStatuses] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'statuses')
    );
    const [searchTerm, setSearchTerm] = useState(() => searchParams.get('q') || '');
    const [timeFilter, setTimeFilter] = useState<'all' | 'today' | 'thisWeek'>(() => {
        const tf = searchParams.get('time');
        if (tf === 'today' || tf === 'thisWeek') return tf;
        return 'all';
    });
    const [selectedClients, setSelectedClients] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'guidelines')
    );
    const [selectedAsps, setSelectedAsps] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'asps')
    );
    const [selectedVersions, setSelectedVersions] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'versions')
    );
    const [selectedMedia, setSelectedMedia] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'media')
    );
    const [selectedOperationCompanies, setSelectedOperationCompanies] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'operationCompanies')
    );
    const [selectedCreators, setSelectedCreators] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'creators')
    );
    const [selectedDeviceModes, setSelectedDeviceModes] = useState(() =>
        parseListParamFromSearchParams(searchParams, 'deviceModes')
    );
    const [itemsPerPage] = useState(6);

    // Stable snapshot for "filters changed" (survives React Strict Mode double-invoking effects on mount).
    const jobListFilterFingerprint = useMemo(
        () =>
            JSON.stringify({
                statuses: [...selectedStatuses].slice().sort(),
                q: searchTerm.trim(),
                time: timeFilter,
                clients: [...selectedClients].slice().sort(),
                asps: [...selectedAsps].slice().sort(),
                versions: [...selectedVersions].slice().sort(),
                media: [...selectedMedia].slice().sort(),
                operationCompanies: [...selectedOperationCompanies].slice().sort(),
                creators: [...selectedCreators].slice().sort(),
                deviceModes: [...selectedDeviceModes].slice().sort(),
            }),
        [
            selectedStatuses,
            searchTerm,
            timeFilter,
            selectedClients,
            selectedAsps,
            selectedVersions,
            selectedMedia,
            selectedOperationCompanies,
            selectedCreators,
            selectedDeviceModes,
        ]
    );
    const prevJobListFilterFingerprintRef = useRef<string | null>(null);
    // Guideline options (id + name) are provided by /jobs/filter-options; no need to call /guidelines here.

    // Keep URL query params in sync when filters change (functional updater avoids stale searchParams).
    useEffect(() => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);

            const setCsv = (key: string, values: string[]) => {
                const cleaned = (values || []).map(String).map((v) => v.trim()).filter(Boolean);
                if (cleaned.length === 0) next.delete(key);
                else next.set(key, cleaned.join(','));
            };

            if (searchTerm.trim()) next.set('q', searchTerm.trim());
            else next.delete('q');

            if (timeFilter && timeFilter !== 'all') next.set('time', timeFilter);
            else next.delete('time');

            setCsv('statuses', selectedStatuses);
            setCsv('guidelines', selectedClients);
            setCsv('versions', selectedVersions);
            setCsv('asps', selectedAsps);
            setCsv('media', selectedMedia);
            setCsv('operationCompanies', selectedOperationCompanies);
            setCsv('creators', selectedCreators);
            setCsv('deviceModes', selectedDeviceModes);

            next.set('page', String(Math.max(1, currentPage)));

            return next;
        }, { replace: true });
    }, [
        setSearchParams,
        searchTerm,
        timeFilter,
        selectedStatuses,
        selectedClients,
        selectedVersions,
        selectedAsps,
        selectedMedia,
        selectedOperationCompanies,
        selectedCreators,
        selectedDeviceModes,
        currentPage
    ]);

    // Map selected Japanese labels → API job statuses; omit param when none or all selected (show every status).
    const jobStatusesForApi = useMemo(() => {
        if (selectedStatuses.length === 0) return undefined;
        const allSelected =
            selectedStatuses.length === ALL_JOB_STATUS_LABELS.length
            && ALL_JOB_STATUS_LABELS.every((s) => selectedStatuses.includes(s));
        if (allSelected) return undefined;
        const mapped = selectedStatuses
            .map((s) => STATUS_LABEL_TO_API[s])
            .filter((s): s is string => Boolean(s));
        return mapped.length > 0 ? mapped : undefined;
    }, [selectedStatuses]);

    // API hooks - using backend pagination
    const selectedVersionNumbers = selectedVersions
        .map((value) => {
            const parsed = parseInt(value, 10);
            return Number.isNaN(parsed) ? null : parsed;
        })
        .filter((value): value is number => value !== null);

    const { data: jobsData, loading: jobsLoading, error: jobsError, refetch: refetchJobs } = useJobs(
        currentPage,
        itemsPerPage,
        jobStatusesForApi,
        timeFilter,
        searchTerm,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        selectedClients,
        selectedVersionNumbers,
        selectedAsps,
        selectedMedia,
        selectedOperationCompanies,
        selectedCreators,
        selectedDeviceModes.length > 0 ? selectedDeviceModes : undefined
    );
    // Job stats are embedded in /jobs response; avoid an extra /jobs/stats request.
    const { data: filterOptionsData, loading: filterOptionsLoading, error: filterOptionsError } = useJobFilterOptions();
    const { mutate: executeJobAction, loading: actionLoading, error: actionError } = useApiMutation(
        (params: { jobId: string; action: string }) => jobsApi.executeAction(params.jobId, params.action)
    );

    // Sync API data with local state and pagination info
    const [paginationInfo, setPaginationInfo] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 6,
        hasNext: false,
        hasPrevious: false
    });

    useEffect(() => {
        if (jobsData && typeof jobsData === 'object') {
            const jobsResponse = jobsData as any;
            console.log('=== JOBS DATA DEBUG ===');
            console.log('Full jobsData:', jobsData);
            console.log('jobsResponse.pagination:', jobsResponse.pagination);
            if (jobsResponse.jobs && Array.isArray(jobsResponse.jobs)) {
                const apiJobs = jobsResponse.jobs.map((item: any) => {
                    const creatorRaw = item.created_by || item.createdBy;
                    let createdBy: JobCreator | undefined;
                    if (creatorRaw) {
                        const creatorId = creatorRaw.id || creatorRaw.username || creatorRaw.email;
                        const displayName =
                            creatorRaw.display_name ||
                            creatorRaw.displayName ||
                            creatorRaw.name ||
                            creatorRaw.username ||
                            creatorRaw.email ||
                            '未設定';
                        if (creatorId) {
                            createdBy = {
                                id: creatorId,
                                name: creatorRaw.name,
                                username: creatorRaw.username,
                                email: creatorRaw.email,
                                role: creatorRaw.role,
                                displayName
                            };
                        }
                    }

                    const guidelineId =
                        item.guideline_id ||
                        item.guidelineId ||
                        item.upload?.guideline_id ||
                        item.client ||
                        item.upload?.client ||
                        'Unknown Guideline';
                    const version = item.version ?? item.upload?.version ?? null;

                    const guidelineName =
                        item.guideline_name ||
                        item.guidelineName ||
                        item.upload?.guideline_name ||
                        null;

                    return {
                        id: item.id,
                        urlCount: item.url_count || item.urlCount,
                        status: item.status,
                        progress: item.progress || 0,
                        startTime: item.start_time ? new Date(item.start_time) : (item.startTime ? new Date(item.startTime) : null),
                        endTime: item.end_time ? new Date(item.end_time) : (item.endTime ? new Date(item.endTime) : undefined),
                        processedUrls: item.processed_urls || item.processedUrls || 0,
                        violationsFound: item.violations_count || item.violationsFound || 0,
                        errors: item.errors || 0,
                        jobName: item.job_name || item.jobName || 'Unnamed Job',
                        guidelineId,
                        guidelineName: guidelineName || guidelineId,
                        version,
                        asp: item.asp || item.upload?.asp || 'Unknown ASP',
                        operationCompany: item.operation_company || item.upload?.operation_company || 'Unknown Operation Company',
                        media: item.media || item.upload?.media || 'Unknown Media', // Support both new direct field and legacy upload.media
                        createdBy,
                        deviceMode: (() => {
                            const raw = item.device_mode ?? item.deviceMode ?? item.upload?.device_mode ?? 'personal_computer';
                            const v = typeof raw === 'string' ? raw.toLowerCase() : String(raw);
                            return (v === 'smart_phone' || v === 'sp' ? 'smart_phone' : 'personal_computer') as 'personal_computer' | 'smart_phone';
                        })(),
                        enableSearch: Boolean(item.enable_search ?? item.enableSearch),
                        searchTimeRangeMode: item.search_time_range_mode ?? item.searchTimeRangeMode ?? null,
                        searchDateFrom: item.search_date_from ?? item.searchDateFrom ?? null,
                        searchDateTo: item.search_date_to ?? item.searchDateTo ?? null,
                    } as ProcessingJob;
                });
                setJobs(apiJobs);
            }

            // Update pagination info from backend response (use client `currentPage` — same render as this fetch)
            if (jobsResponse.pagination) {
                console.log('Setting pagination info:', jobsResponse.pagination);
                const totalPages =
                    jobsResponse.pagination.total_pages || jobsResponse.pagination.totalPages || 1;
                const totalItems =
                    jobsResponse.pagination.total_items || jobsResponse.pagination.totalItems || 0;
                const itemsPerPage =
                    jobsResponse.pagination.items_per_page || jobsResponse.pagination.itemsPerPage || 6;
                setPaginationInfo({
                    currentPage,
                    totalPages,
                    totalItems,
                    itemsPerPage,
                    hasNext: currentPage < totalPages,
                    hasPrevious: currentPage > 1,
                });
            } else {
                console.log('No pagination data in response');
            }
        }
    }, [jobsData]);

    // Real-time updates for processing jobs (refresh data periodically)
    useEffect(() => {
        const interval = setInterval(() => {
            // Only refresh if there are processing jobs
            const hasProcessingJobs = jobs.some(job => job.status === 'processing');
            if (hasProcessingJobs) {
                refetchJobs();
            }
        }, 300000); // Refresh every 5 minutes

        return () => clearInterval(interval);
    }, [jobs, refetchJobs]);

    // Helper functions for time filtering (now handled by backend)
    // const isToday = (date: Date) => {
    //     const today = new Date();
    //     return date.toDateString() === today.toDateString();
    // };

    // const isThisWeek = (date: Date) => {
    //     const today = new Date();
    //     const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    //     return date >= weekAgo;
    // };

    // Backend handles filtering and pagination, so we use jobs directly
    const paginatedJobs = jobs;

    // Debug logging (can be removed in production)
    console.log('Backend Pagination Debug:', {
        totalJobs: jobs.length,
        paginationInfo,
        currentPage,
        itemsPerPage
    });

    // Reset to first page when filters change (fingerprint avoids resetting on Strict Mode's duplicate mount effect run).
    useEffect(() => {
        if (prevJobListFilterFingerprintRef.current === null) {
            prevJobListFilterFingerprintRef.current = jobListFilterFingerprint;
            return;
        }
        if (prevJobListFilterFingerprintRef.current === jobListFilterFingerprint) {
            return;
        }
        prevJobListFilterFingerprintRef.current = jobListFilterFingerprint;
        setCurrentPage(1);
    }, [jobListFilterFingerprint]);

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'pending':
                return <Clock className="status-icon pending" />;
            case 'processing':
                return <Activity className="status-icon processing" />;
            case 'completed':
                return <CheckCircle className="status-icon completed" />;
            case 'failed':
                return <AlertTriangle className="status-icon failed" />;
            default:
                return <Clock className="status-icon" />;
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'pending':
                return '保留中';
            case 'processing':
                return '処理中';
            case 'completed':
                return '完了';
            case 'failed':
                return '失敗';
            default:
                return '不明';
        }
    };

    const formatDuration = (startTime: Date | null, endTime?: Date) => {
        if (!startTime || isNaN(startTime.getTime())) {
            return '0m 0s';
        }

        const end = endTime || new Date();
        const duration = end.getTime() - startTime.getTime();
        const minutes = Math.floor(duration / 60000);
        const seconds = Math.floor((duration % 60000) / 1000);
        return `${minutes}m ${seconds}s`;
    };

    const formatStartTime = (startTime: Date | null) => {
        if (!startTime || isNaN(startTime.getTime())) {
            return '未開始';
        }

        const now = new Date();
        // const diff = now.getTime() - startTime.getTime();
        const isToday = startTime.toDateString() === now.toDateString();

        if (isToday) {
            // Show time for today's jobs
            return startTime.toLocaleTimeString('ja-JP', {
                hour: '2-digit',
                minute: '2-digit'
            });
        } else {
            // Show date and time for older jobs
            return startTime.toLocaleDateString('ja-JP', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    const getPageNumbers = (current: number, total: number): (number | '...')[] => {
        if (total <= 7) {
            return Array.from({ length: total }, (_, i) => i + 1);
        }
        const delta = 2;
        const rangeStart = Math.max(2, current - delta);
        const rangeEnd = Math.min(total - 1, current + delta);
        const core: number[] = [1];
        for (let i = rangeStart; i <= rangeEnd; i++) core.push(i);
        core.push(total);

        const result: (number | '...')[] = [];
        let prev: number | undefined;
        for (const page of core) {
            if (prev !== undefined) {
                if (page - prev === 2) result.push(prev + 1);
                else if (page - prev > 2) result.push('...');
            }
            result.push(page);
            prev = page;
        }
        return result;
    };

    const handleJobAction = async (jobId: string, action: 'delete') => {
        try {
            const result = await executeJobAction({ jobId, action });

            if (result) {
                // Refresh jobs data after successful action
                await refetchJobs();
                toast.success('ジョブの削除が完了しました', { title: '削除完了' });
            }
        } catch (error) {
            console.error(`Job ${action} failed:`, error);
            toast.error('ジョブの削除に失敗しました', { title: '削除失敗' });
        }
    };

    // Stats are embedded in /jobs response
    const stats = jobsData && typeof jobsData === 'object' && 'stats' in (jobsData as any)
        ? (jobsData as any).stats
        : {
            total: paginationInfo.totalItems,
            pending: 0,
            inprogress: 0,
            completed: 0,
            failed: 0
        };

    // Extract filter options
    const filterOptions = filterOptionsData && typeof filterOptionsData === 'object'
        ? (filterOptionsData as any)?.data || {}
        : {
            guidelines: [],
            versions: [],
            asps: [],
            media_names: [],
            operation_companies: [],
            creators: []
        };

    const creatorOptions = (filterOptions.creators || []).map((creator: any) => ({
        label: creator.display_name || creator.name || creator.username || creator.email || creator.id,
        value: creator.id
    }));

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">処理ジョブ</h1>
                <p className="page-description">
                    違反検出のためのURL処理ジョブを監視・管理します。
                </p>
            </div>

            {/* Stats Overview */}
            <div className="stats-section">
                {/* Error Display */}
                {(jobsError || actionError) && (
                    <div className="error-state">
                        <AlertTriangle size={20} />
                        <span>
                            {jobsError || actionError}
                        </span>
                    </div>
                )}

                {/* Loading State for Stats */}
                {jobsLoading && (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>統計情報を読み込み中...</span>
                    </div>
                )}

                {!jobsLoading && (
                    <div className="stats-grid">
                        <div className="stat-card">
                            <div className="stat-icon">
                                <Activity size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.total}</div>
                                <div className="stat-label">総ジョブ数</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon pending">
                                <Clock size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.pending}</div>
                                <div className="stat-label">保留中</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon active">
                                <Activity size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.inprogress}</div>
                                <div className="stat-label">処理中</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon completed">
                                <CheckCircle size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.completed}</div>
                                <div className="stat-label">完了</div>
                            </div>
                        </div>

                        <div className="stat-card">
                            <div className="stat-icon failed">
                                <AlertTriangle size={24} />
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{stats.failed}</div>
                                <div className="stat-label">失敗</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Filters and Search */}
            <div className="filters-section">
                <div className="filters-row">
                    <div className="search-box">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            placeholder="ジョブを検索..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>


                    <div className="filter-controls">
                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <FileText size={12} />
                                    ガイドライン
                                </span>
                            }
                            options={(filterOptions.guidelines || []).map((g: any) => ({
                                label: g.name || g.id,
                                value: g.id,
                            }))}
                            selectedValues={selectedClients}
                            onSelectionChange={setSelectedClients}
                            placeholder="すべてのガイドライン"
                            searchPlaceholder="ガイドラインを検索..."
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <History size={12} />
                                    バージョン
                                </span>
                            }
                            options={(filterOptions.versions || []).map((version: number) => version.toString())}
                            selectedValues={selectedVersions}
                            onSelectionChange={setSelectedVersions}
                            placeholder="すべてのバージョン"
                            searchPlaceholder="バージョンを検索..."
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Building size={12} />
                                    ASP
                                </span>
                            }
                            options={filterOptions.asps || []}
                            selectedValues={selectedAsps}
                            onSelectionChange={setSelectedAsps}
                            placeholder="すべてのASP"
                            searchPlaceholder="ASPを検索..."
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Building size={12} />
                                    運営会社
                                </span>
                            }
                            options={filterOptions.operation_companies || []}
                            selectedValues={selectedOperationCompanies}
                            onSelectionChange={setSelectedOperationCompanies}
                            placeholder="すべての運営会社"
                            searchPlaceholder="運営会社を検索..."
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Building size={12} />
                                    媒体名
                                </span>
                            }
                            options={filterOptions.media_names || []}
                            selectedValues={selectedMedia}
                            onSelectionChange={setSelectedMedia}
                            placeholder="すべての媒体"
                            searchPlaceholder="媒体名を検索..."
                            loading={filterOptionsLoading}
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Monitor size={12} />
                                    パトロールデバイス
                                </span>
                            }
                            options={[
                                { label: 'PC', value: 'personal_computer' },
                                { label: 'モバイル', value: 'smart_phone' },
                            ]}
                            selectedValues={selectedDeviceModes}
                            onSelectionChange={setSelectedDeviceModes}
                            placeholder="すべてのデバイス"
                            searchPlaceholder="デバイスを検索..."
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <CircleDot size={12} />
                                    ステータス
                                </span>
                            }
                            options={ALL_JOB_STATUS_LABELS}
                            selectedValues={selectedStatuses}
                            onSelectionChange={setSelectedStatuses}
                            placeholder="すべてのステータス"
                            searchPlaceholder="ステータスを検索..."
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Timer size={12} />
                                    アップロード時
                                </span>
                            }
                            options={['今日', '今週']}
                            selectedValues={timeFilter === 'all' ? [] : [timeFilter === 'today' ? '今日' : '今週']}
                            onSelectionChange={(values) => {
                                if (values.length === 0) {
                                    setTimeFilter('all');
                                } else {
                                    setTimeFilter(values[0] === '今日' ? 'today' : 'thisWeek');
                                }
                            }}
                            placeholder="すべての時間"
                            searchPlaceholder="時間を検索..."
                        />

                        <MultiSelectFilter
                            label={
                                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <UserCircle size={12} />
                                    作成者
                                </span>
                            }
                            options={creatorOptions}
                            selectedValues={selectedCreators}
                            onSelectionChange={setSelectedCreators}
                            placeholder="すべての作成者"
                            searchPlaceholder="作成者を検索..."
                            loading={filterOptionsLoading}
                        />
                    </div>
                </div>
            </div>

            {/* Jobs List */}
            <div className="jobs-section">
                {/* Loading State for Jobs */}
                {jobsLoading && (
                    <div className="loading-state">
                        <Loader2 size={20} className="animate-spin" />
                        <span>ジョブ一覧を読み込み中...</span>
                    </div>
                )}

                {!jobsLoading && jobs.length === 0 ? (
                    <div className="empty-state">
                        <Activity size={48} className="empty-icon" />
                        <h3>ジョブが見つかりません</h3>
                        <p>現在のフィルターに一致する処理ジョブがありません</p>
                    </div>
                ) : !jobsLoading ? (
                    <>
                        <div className="jobs-list">
                            {paginatedJobs.map((job) => (
                                <div key={job.id} className={`job-item ${job.status}`}>
                                    <div className="job-header">
                                        <div className="job-info">
                                            <div className="job-icon">
                                                {getStatusIcon(job.status)}
                                            </div>
                                            <div className="job-details">
                                                <div className="job-name-row">
                                                    <h3 className="job-name">{job.jobName || 'Unnamed Job'}</h3>
                                                </div>
                                                <div className="job-client-info">
                                                    <span className="job-guideline">
                                                        <FileText size={11} />
                                                        ガイドライン: {job.guidelineName || job.guidelineId || '未設定'}
                                                    </span>
                                                    <span className="job-version">
                                                        <History size={11} />
                                                        バージョン: {job.version ? `v${job.version}` : '未設定'}
                                                    </span>
                                                    <span className="job-asp">
                                                        <Building size={11} />
                                                        ASP: {job.asp}
                                                    </span>
                                                    <span className="job-operation-company">
                                                        <Building size={11} />
                                                        運営会社: {job.operationCompany || '未設定'}
                                                    </span>
                                                    <span className="job-media">
                                                        <Building size={11} />
                                                        媒体名: {job.media}
                                                    </span>
                                                    {job.createdBy && (
                                                        <span className="job-creator">
                                                            <UserCircle size={11} />
                                                            作成者: {job.createdBy.displayName}
                                                        </span>
                                                    )}
                                                    {job.enableSearch && (
                                                        <span
                                                            className="job-search-tag"
                                                            title="カスタム検索モード"
                                                        >
                                                            <Search size={11} aria-hidden />
                                                            検索
                                                        </span>
                                                    )}
                                                    {job.enableSearch && (
                                                        <span className="job-search-range">
                                                            <Calendar size={11} />
                                                            検索期間: {formatJobSearchRangeText(job)}
                                                        </span>
                                                    )}
                                                    <span className="job-device">
                                                        {(() => {
                                                            const rawDevice = (job as any).device_mode ?? job.deviceMode ?? 'personal_computer';
                                                            const deviceMode = String(rawDevice).toLowerCase() === 'smart_phone' || String(rawDevice).toLowerCase() === 'sp' ? 'smart_phone' : 'personal_computer';
                                                            const isMobile = deviceMode === 'smart_phone';
                                                            return (
                                                                <>
                                                                    {isMobile ? <Smartphone size={11} /> : <Monitor size={11} />}
                                                                    デバイス: {isMobile ? 'モバイル' : 'PC'}
                                                                </>
                                                            );
                                                        })()}
                                                    </span>
                                                </div>
                                                <div className="job-meta">
                                                    <span className="job-urls">{job.urlCount} URL</span>
                                                    <span className="job-start-time">
                                                        開始: {formatStartTime(job.startTime)}
                                                    </span>
                                                    <span className="job-duration">
                                                        {formatDuration(job.startTime, job.endTime)}
                                                    </span>
                                                    <span className={`job-status ${job.status}`}>
                                                        {getStatusText(job.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="job-actions">
                                            {/* Show results button for all jobs except pending */}
                                            {job.status !== 'pending' && (
                                                <Link
                                                    to={`/results?jobId=${job.id}`}
                                                    className="btn btn-primary btn-sm"
                                                >
                                                    <Eye size={16} />
                                                    結果を表示
                                                </Link>
                                            )}

                                            <button
                                                onClick={() => handleJobAction(job.id, 'delete')}
                                                className="btn btn-danger btn-sm"
                                                title="ジョブを削除"
                                                disabled={actionLoading}
                                            >
                                                {actionLoading ? (
                                                    <Loader2 size={16} className="animate-spin" />
                                                ) : (
                                                    <Trash2 size={16} />
                                                )}
                                                削除
                                            </button>
                                        </div>
                                    </div>

                                    {job.status === 'processing' && (
                                        <div className="job-progress">
                                            <div className="progress-header">
                                                <span>進捗: {job.progress.toFixed(1)}%</span>
                                                <span>{job.processedUrls} / {job.urlCount} URL</span>
                                            </div>
                                            <div className="progress-bar md enhanced no-margin">
                                                <div
                                                    className="progress-fill enhanced"
                                                    style={{ width: `${job.progress}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="job-stats">
                                        <div className="stat-item">
                                            <span className="stat-label">処理済み:</span>
                                            <span className="stat-value">{job.processedUrls}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">違反:</span>
                                            <span className="stat-value violations">{job.violationsFound}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">エラー:</span>
                                            <span className="stat-value errors">{job.errors}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        {paginationInfo.totalPages > 1 && (
                            <div className="pagination">
                                <div className="pagination-info">
                                    {paginationInfo.totalItems} ジョブ中 {((paginationInfo.currentPage - 1) * paginationInfo.itemsPerPage) + 1}-{Math.min(paginationInfo.currentPage * paginationInfo.itemsPerPage, paginationInfo.totalItems)} を表示
                                </div>
                                <div className="pagination-controls">
                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={!paginationInfo.hasPrevious}
                                    >
                                        <ChevronLeft size={16} />
                                        前へ
                                    </button>

                                    <div className="pagination-numbers">
                                        {getPageNumbers(paginationInfo.currentPage, paginationInfo.totalPages).map((page, idx) =>
                                            page === '...'
                                                ? <span key={`ellipsis-${idx}`} className="pagination-ellipsis">…</span>
                                                : <button
                                                    key={page}
                                                    className={`pagination-number ${paginationInfo.currentPage === page ? 'active' : ''}`}
                                                    onClick={() => setCurrentPage(page)}
                                                >
                                                    {page}
                                                </button>
                                        )}
                                    </div>

                                    <button
                                        className="pagination-btn"
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, paginationInfo.totalPages))}
                                        disabled={!paginationInfo.hasNext}
                                    >
                                        次へ
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default JobListPage;
